import { useState, useEffect } from "react";
import { Lock, Unlock, Loader2, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";
import { useApp } from "@/context/AppCtx";

type TokenGateStatus = "loading" | "locked" | "unlocked" | "error";

interface TokenGateConfig {
  requiredTokenMint: string; // Solana token mint address
  requiredAmount: number; // Minimum tokens required
  alternativePayment?: {
    solAmount: number; // Alternative SOL payment
    paymentAddress: string; // Payment address
  };
}

const DEFAULT_TOKEN_GATE_CONFIG: TokenGateConfig = {
  requiredTokenMint: "So11111111111111111111111111111111111111112", // Default to SOL for now
  requiredAmount: 0.1, // 0.1 SOL minimum
  alternativePayment: {
    solAmount: 0.05, // 0.05 SOL alternative payment
    paymentAddress: "YourPaymentAddressHere" // Replace with actual payment address
  }
};

export function TokenGate() {
  const { heliosKey } = useApp();
  const [status, setStatus] = useState<TokenGateStatus>("loading");
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);
  const [config] = useState<TokenGateConfig>(DEFAULT_TOKEN_GATE_CONFIG);

  useEffect(() => {
    checkTokenGate();
  }, [heliosKey]);

  const checkTokenGate = async () => {
    setStatus("loading");
    
    try {
      // In a real implementation, this would:
      // 1. Check if user has connected wallet
      // 2. Query token balance for required token
      // 3. Verify payment status if alternative payment used
      
      // For now, simulate the check
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate unlocked state for development
      // In production, this would check actual token holdings
      setStatus("unlocked");
      
    } catch (error) {
      console.error("Token gate check failed:", error);
      setStatus("error");
    }
  };

  const handlePayment = () => {
    // In production, this would open a payment modal or redirect to payment processor
    setShowPaymentInfo(true);
  };

  if (status === "loading") {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-6">
            <Loader2 className="size-8 animate-spin text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-white text-center mb-2">Checking Access</h2>
          <p className="text-white/60 text-center text-sm">Verifying your access permissions...</p>
        </div>
      </div>
    );
  }

  if (status === "unlocked") {
    return null; // Don't show anything when unlocked
  }

  if (status === "error") {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-neutral-900 border border-red-500/30 rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-6">
            <AlertCircle className="size-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white text-center mb-2">Access Error</h2>
          <p className="text-white/60 text-center text-sm mb-6">
            Unable to verify your access. Please try again or contact support.
          </p>
          <button
            onClick={checkTokenGate}
            className="w-full flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-xl font-semibold hover:bg-emerald-500/20 transition-all"
          >
            <RefreshCw className="size-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Locked state
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="flex items-center justify-center mb-6">
          <Lock className="size-8 text-amber-400" />
        </div>
        
        <h2 className="text-xl font-bold text-white text-center mb-2">Premium Access Required</h2>
        <p className="text-white/60 text-center text-sm mb-6">
          Hold {config.requiredAmount} SOL or pay {config.alternativePayment?.solAmount} SOL to unlock full trading features.
        </p>

        <div className="space-y-3 mb-6">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">Option 1: Hold Token</span>
              <span className="text-emerald-400 text-sm font-bold">{config.requiredAmount} SOL</span>
            </div>
            <p className="text-white/50 text-xs">
              Maintain minimum balance in your wallet
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">Option 2: One-time Payment</span>
              <span className="text-emerald-400 text-sm font-bold">{config.alternativePayment?.solAmount} SOL</span>
            </div>
            <p className="text-white/50 text-xs">
              Pay once for permanent access
            </p>
          </div>
        </div>

        {showPaymentInfo && config.alternativePayment && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-emerald-400 font-bold text-sm mb-2">Payment Address</h3>
            <p className="text-white/80 font-mono text-xs break-all mb-3">
              {config.alternativePayment.paymentAddress}
            </p>
            <p className="text-white/50 text-xs">
              Send {config.alternativePayment.solAmount} SOL to this address. Access will be unlocked automatically after confirmation.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handlePayment}
            className="w-full flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-xl font-semibold hover:bg-emerald-500/20 transition-all"
          >
            <Unlock className="size-4" />
            {showPaymentInfo ? "Hide Payment Info" : "Show Payment Options"}
          </button>
          
          <button
            onClick={checkTokenGate}
            className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all"
          >
            <RefreshCw className="size-4" />
            Check Access Status
          </button>
        </div>

        <p className="text-white/40 text-xs text-center mt-6">
          Need help? Contact support for assistance.
        </p>
      </div>
    </div>
  );
}

export function TokenGateBanner() {
  const [status, setStatus] = useState<TokenGateStatus>("loading");
  const [userBalance, setUserBalance] = useState<number | null>(null);

  useEffect(() => {
    // Simulate check
    setStatus("unlocked");
    setUserBalance(0.15);
  }, []);

  if (status === "unlocked") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
        <CheckCircle className="size-3.5 text-emerald-400" />
        <span className="text-[10px] font-medium text-emerald-300">
          Premium Active
        </span>
        {userBalance && (
          <span className="text-[9px] text-emerald-400/70">
            {userBalance.toFixed(3)} SOL
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 cursor-pointer hover:bg-amber-500/20 transition-all">
      <Lock className="size-3.5 text-amber-400" />
      <span className="text-[10px] font-medium text-amber-300">
        Upgrade Required
      </span>
    </div>
  );
}
